import { TimeoutError } from "@microsoft/signalr";
import { Api } from "../../Api";
import { AsyncSemaphore } from "../../AsyncSemaphore";
import { DataFrame, Method, Status } from "../../DataFrame";
import { ExalusConnectionService } from "../ExalusConnectionService";
import { IExalusConnectionService } from "../IExalusConnectionService";
import { IUpdatesProvider, ProviderType, UpdatesProviderNotFound } from "./IUpdatesProvider";
import { IUpdateProviderService } from "./UpdateProviderServices/IUpdateProviderService";
import { DependencyContainer } from "../../DependencyContainer";

export class UpdatesProvider implements IUpdatesProvider {
    public static readonly ServiceName: string = "UpdateProvider";

    private _services: Map<string, IUpdateProviderService> = new Map();
    private _runtimeInfo: SoftwareRuntimeInfo = new SoftwareRuntimeInfo();
    private _hardwareInfo: HardwareInfo = new HardwareInfo();
    private _semaphore: AsyncSemaphore = new AsyncSemaphore(1);

    private _isRuntimeInfoInitialized: boolean = false;
    private _isHardwareInfoInitialized: boolean = false;

    GetServiceName(): string {
        return UpdatesProvider.ServiceName;
    }

    RegisterUpdatesProvider(service: IUpdateProviderService): void {
        DependencyContainer.Log?.Debug(UpdatesProvider.ServiceName, `Registering new update provider: ${service.GetUpdateProviderName()}`);
        this._services.set(service.GetUpdateProviderName(), service);
    }

    async GetUpdatesProviderAsync<T extends IUpdateProviderService>(serviceName: string): Promise<T> {
        DependencyContainer.Log?.Debug(UpdatesProvider.ServiceName, `Getting update provider [${serviceName}] by GetUpdateProviderAsync<T>()`);
        const service = this._services.get(serviceName);

        if (service == null)
            throw new UpdatesProviderNotFound("Cannot get update provider! Requested update provider has not been registered.");

        return service as unknown as T;
    }

    async GetUpdatesProvidersAsync<T extends IUpdateProviderService>(updateProviderType: ProviderType): Promise<T[]> {
        DependencyContainer.Log?.Debug(UpdatesProvider.ServiceName, `Getting update providers with type ${updateProviderType} by GetUpdateProvidersAsync<T>()`);
        let services: IUpdateProviderService[] = [];

        services = [...this._services.values()].filter(provider => provider.ProviderType == updateProviderType);

        if (services.length == 0)
            throw new UpdatesProviderNotFound("Cannot get update providers! Requested update providers has not been registered.");

        return services as unknown[] as T[];
    }

    async GetUpdatesProvidersByProtocolAsync<T extends IUpdateProviderService>(protocolGuid: string, updateProviderType: ProviderType, apiVersion?: string | undefined): Promise<T[]> {
        const regGuid = new RegExp("^[{]?[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}[}]?$");

        if (regGuid.test(protocolGuid as string)) {
            DependencyContainer.Log?.Debug(UpdatesProvider.ServiceName, `Getting update providers with protocol guid: [${protocolGuid as string}] by GetUpdateProvidersByProtocolAsync<T>()`);
            let services: IUpdateProviderService[] = [];

            if (apiVersion != null)
                services = [...this._services.values()].filter(provider => provider.ProtocolGuid == protocolGuid && provider.ProviderType == updateProviderType && provider.AvailableExtensionVersion == apiVersion);
            else
                services = [...this._services.values()].filter(provider => provider.ProtocolGuid == protocolGuid && provider.ProviderType == updateProviderType);

            if (services.length == 0)
                throw new UpdatesProviderNotFound("Cannot get update providers! Requested update providers has not been registered.");

            return services as unknown[] as T[];
        }
        else
            throw new UpdatesProviderNotFound("Cannot get update providers! Bad parameters.");
    }

    async GetUpdatesProvidersByExtensionAsync<T extends IUpdateProviderService>(extensionGuid: string, updateProviderType: ProviderType, apiVersion?: string | undefined): Promise<T[]> {
        const regGuid = new RegExp("^[{]?[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}[}]?$");

        if (regGuid.test(extensionGuid as string)) {
            DependencyContainer.Log?.Debug(UpdatesProvider.ServiceName, `Getting update providers with extension guid: [${extensionGuid as string}] by GetUpdateProvidersByExtensionAsync<T>()`);
            let services: IUpdateProviderService[] = [];

            if (apiVersion != null)
                services = [...this._services.values()].filter(provider => provider.ExtensionGuid == extensionGuid && provider.ProviderType == updateProviderType && provider.AvailableExtensionVersion == apiVersion);
            else
                services = [...this._services.values()].filter(provider => provider.ExtensionGuid == extensionGuid && provider.ProviderType == updateProviderType);

            if (services.length == 0)
                throw new UpdatesProviderNotFound("Cannot get update providers! Requested update providers has not been registered.");

            return services as unknown[] as T[];
        }
        else
            throw new UpdatesProviderNotFound("Cannot get update providers! Bad parameters.");
    }

    async GetSoftwareRuntimeInfoAsync(reloadCachedData = false): Promise<SoftwareRuntimeInfo> {
        var lock = await this._semaphore.AcquireAsync();

        try {
            if (!this._isRuntimeInfoInitialized || reloadCachedData) {
                await this.GetRuntimeControllerInfoAsync();
            }

            return this._runtimeInfo;
        }
        finally {
            lock.Release();
        }
    }

    protected async GetRuntimeControllerInfoAsync() {
        DependencyContainer.Log?.Debug(UpdatesProvider.ServiceName, `Getting runtime version info from controller.`);

        try {
            const result = await Api.Get<IExalusConnectionService>(ExalusConnectionService.ServiceName).SendAndWaitForResponseAsync<SoftwareRuntimeInfo>(new GetControllerRuntimeInfoRequest(), 16000, false);

            if (result == null || result.Status == null)
                throw new CannotGetRuntimeInfo(`Cannot get runtime info, controller response result is empty."`, CannotGetRuntimeErrorCode.EmptyResponse);

            if (result.Status != Status.OK)
                throw new CannotGetRuntimeInfo(`Cannot get runtime info, controller responded with status ${result.Status}.`, CannotGetRuntimeErrorCode.WrongResponseStatus);

            if (result.Data == null)
                throw new CannotGetRuntimeInfo(`Cannot get runtime info, controller responded with status OK but response does not contains data.`, CannotGetRuntimeErrorCode.NoDataInResponse);

            this._runtimeInfo = result.Data;
            this._isRuntimeInfoInitialized = true;
        } 
        catch (error) {
            if (error instanceof TimeoutError)
                throw new CannotGetRuntimeInfo(`Cannot get runtime info, controller response timeout.`, CannotGetRuntimeErrorCode.Timeout);
            else
                throw error;
        }
    }

    async GetHardwareInfoAsync(reloadCachedData = false): Promise<HardwareInfo> {
        if (!this._isHardwareInfoInitialized || reloadCachedData) {
            await this.GetHardwareControllerInfoAsync();
            return this._hardwareInfo;
        }
        return this._hardwareInfo;
    }

    protected async GetHardwareControllerInfoAsync() {
        DependencyContainer.Log?.Debug(UpdatesProvider.ServiceName, `Getting hardware info from controller.`);

        const result = await Api.Get<IExalusConnectionService>(ExalusConnectionService.ServiceName).SendAndWaitForResponseAsync<HardwareInfo>(new GetControllerHardwareInfoRequest(), 8_000, false);
        if (result == null || result.Status == null)
            throw new CannotGetRuntimeInfo(`Cannot get hardware info, controller response result is empty."`, CannotGetRuntimeErrorCode.EmptyResponse);

        if (result.Status != Status.OK)
            throw new CannotGetRuntimeInfo(`Cannot get hardware info, controller responded with status ${result.Status}.`, CannotGetRuntimeErrorCode.WrongResponseStatus);

        if (result.Data == null)
            throw new CannotGetRuntimeInfo(`Cannot get hardware info, controller responded with status OK but response does not contains data.`, CannotGetRuntimeErrorCode.NoDataInResponse);

        this._hardwareInfo = result.Data;
        this._isHardwareInfoInitialized = true;
    }
}

//RuntimeInfo
export class SoftwareRuntimeInfo {
    BaseLinuxVersion: string = "";
    BaseMonoVersion: string = "";
    RuntimeVersion: string = "";
    UpdateChannel: string = "";
    SoftwareVersion: string = "";
}

class GetControllerRuntimeInfoRequest extends DataFrame<any>{
    constructor() {
        super();
        this.Resource = "/controller/software/info";
        this.Method = Method.Get;
    }
}

//HardwareInfo
export class HardwareInfo {
    GetControllerHardwareVersion: number = 0;
    DevicePIN: string = "";
    GetControllerSerialNumber: string = "";
    HostnamePrefix: string = "";
    DefaultStaticIpAddress: string = "";
    GetAvailableHardware: Hardware[] = [];
    ControllerSecret: string = "";
}

export class Hardware {
    HardwareGUID: string = "";
    DevicePath: string = "";
    DisplayName: string = "";
    HardwareSerialNumber: string = "";
    HardwareVersion: number = 0;
    SoftwareVersion: number = 0;
    Configuration: string = "";
    ControllerSecret: string = "";
}

class GetControllerHardwareInfoRequest extends DataFrame<any>{
    constructor() {
        super();
        this.Resource = "/controller/hardware/info";
        this.Method = Method.Get;
    }
}

//Errors
export class CannotGetRuntimeInfo extends Error {
    code: CannotGetRuntimeErrorCode = 0;
    constructor(public message: string, code: CannotGetRuntimeErrorCode) {
        super(message);
        this.name = "CannotGetRuntimeInfo";
        this.code = code;
    }
}

export enum CannotGetRuntimeErrorCode {
    Unknown = 0,
    EmptyResponse = 1,
    NoDataInResponse = 2,
    WrongResponseStatus = 3,
    Timeout = 4
}
